/**
 * Blocul de rezervare a unei mese.
 *
 * De ce arata asa si nu ca un formular obisnuit:
 *
 *  - **Limitele vin de la restaurant, nu de la platforma.** Numarul minim si
 *    maxim de persoane, cu cate zile in avans si cu cate ore inainte se poate
 *    rezerva, ocaziile — toate sunt setari din POS-ul lui. Scrise pe platforma,
 *    ar fi produs refuz la ultimul pas, dupa ce clientul si-a completat numele
 *    si telefonul.
 *  - **Orele se cer de la restaurant, nu se genereaza.** O grila de sferturi de
 *    ora construita aici ar oferi ore in care bucataria e inchisa sau salonul e
 *    plin.
 *  - **Restaurantul se alege din date reale.** Blocul nu are un vendor scris in
 *    config: cere lista, iar cand piata are un singur restaurant cu rezervari il
 *    alege singur, fara sa mai puna o intrebare cu un singur raspuns.
 *  - **`pending` nu se rotunjeste la „gata".** Un client care crede ca are masa
 *    rezervata si nu are se prezinta la usa.
 */

import { useEffect, useMemo, useState } from "react";

import { blockText, type ReservationCreateResponse, type ReservationVendorDto } from "@deliveryapp/shared";

import { createReservation, fetchReservationSlots, fetchReservationVendors } from "../api/client";
import { useBootstrap } from "../components/BootstrapProvider";
import { Alert, Button, Field, TextArea } from "../components/ui";
import { toApiError, useLoadable } from "../components/useLoadable";
import { paragraphs, type BlockProps } from "./context";
import { BlockHeading, BlockSection } from "./parts";

/** `AAAA-LL-ZZ` din data locala, nu din UTC: la 23:30 in Romania, `toISOString` da ziua urmatoare. */
function isoDay(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function addDays(base: Date, days: number): Date {
  const copy = new Date(base.getTime());
  copy.setDate(copy.getDate() + days);
  return copy;
}

interface FormState {
  date: string;
  time: string;
  partySize: string;
  name: string;
  phone: string;
  email: string;
  occasion: string;
  notes: string;
}

const EMPTY_FORM: FormState = {
  date: "",
  time: "",
  partySize: "2",
  name: "",
  phone: "",
  email: "",
  occasion: "",
  notes: "",
};

export function ReservationsBlock({ block, headingLevel }: BlockProps) {
  const { config, marketKey } = useBootstrap();
  const supportPhone = config.identity.supportPhone ?? "";
  const vendorsLoad = useLoadable((signal) => fetchReservationVendors(marketKey, signal), [marketKey]);
  const vendors = useMemo<ReservationVendorDto[]>(() => vendorsLoad.data?.vendors ?? [], [vendorsLoad.data]);

  const [vendorSlug, setVendorSlug] = useState("");
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<ReservationCreateResponse | null>(null);

  // Un singur restaurant cu rezervari: se alege singur. Un select cu o singura
  // optiune e o intrebare cu un singur raspuns, pusa fiecarui client.
  useEffect(() => {
    if (vendors.length === 0) return;
    setVendorSlug((current) => (vendors.some((item) => item.vendorSlug === current) ? current : (vendors[0]?.vendorSlug ?? "")));
  }, [vendors]);

  const vendor = vendors.find((item) => item.vendorSlug === vendorSlug) ?? null;

  const today = useMemo(() => new Date(), []);
  /**
   * Prima zi in care restaurantul mai poate primi rezervarea.
   *
   * `minAdvanceHours` era citit din contract si ignorat: un restaurant care cere
   * 24 de ore in avans lasa clientul sa aleaga AZI, sa completeze tot, si sa
   * primeasca o lista goala de ore fara sa afle de ce. Daca fereastra de avans nu
   * mai incape in ziua curenta, ziua minima trece pe maine.
   */
  const minDate = useMemo(() => {
    if (vendor === null) return isoDay(today);
    const earliest = new Date(today.getTime() + Math.max(0, vendor.minAdvanceHours) * 3_600_000);
    return isoDay(earliest.getTime() > today.getTime() ? earliest : today);
  }, [today, vendor]);
  const rawMaxDate = vendor === null
    ? minDate
    : isoDay(addDays(today, Math.max(0, vendor.advanceBookingDays)));
  /**
   * Fereastra se poate INCHIDE: un restaurant care primeste rezervari doar in ziua
   * curenta (`advanceBookingDays = 0`) si cere doua ore in avans nu mai are nicio
   * zi valabila la 23:00. Cu `min > max`, campul de data nu accepta nimic si nu
   * spune de ce — clientul ramane in fata unui formular care nu se poate completa.
   */
  const windowClosed = rawMaxDate < minDate;
  const maxDate = windowClosed ? minDate : rawMaxDate;

  const slotsLoad = useLoadable(
    (signal) => {
      const size = Number(form.partySize);
      if (vendor === null || form.date.length === 0 || !Number.isFinite(size) || size <= 0) {
        return Promise.resolve(null);
      }
      return fetchReservationSlots(marketKey, vendor.vendorSlug, { date: form.date, partySize: size }, signal);
    },
    [marketKey, vendor?.vendorSlug, form.date, form.partySize],
  );

  const slots = slotsLoad.data?.slots ?? [];
  const freeSlots = slots.filter((slot) => slot.available);
  /**
   * Numarul de persoane e scris de mana, deci trece prin starea „gol" de fiecare
   * data cand cineva sterge „2" ca sa scrie „6". `Number("")` e 0, iar pe zero nu
   * se cer sloturi — fara verificarea asta, exact intre cele doua apasari ecranul
   * afirma „nu mai e nicio ora libera", o afirmatie falsa despre restaurant.
   */
  const partySizeValid = Number.isFinite(Number(form.partySize)) && Number(form.partySize) > 0;

  // Ora aleasa nu are voie sa supravietuiasca schimbarii zilei sau a numarului de
  // persoane: ar fi plecat spre restaurant o ora care intre timp nu mai e libera.
  useEffect(() => {
    setForm((current) => (current.time.length === 0 ? current : { ...current, time: "" }));
  }, [form.date, form.partySize, vendorSlug]);

  // Ziua aleasa nu are voie sa ramana in afara ferestrei restaurantului dupa ce
  // clientul schimba localul: altfel formularul trimite o data pe care POS-ul o
  // refuza, iar clientul citeste un refuz pentru ceva ce nu a facut el.
  useEffect(() => {
    setForm((current) => (current.date !== "" && current.date < minDate ? { ...current, date: "" } : current));
  }, [minDate]);

  const set = (key: keyof FormState, value: string): void => {
    setForm((current) => ({ ...current, [key]: value }));
    setProblem(null);
  };

  const submit = async (): Promise<void> => {
    if (vendor === null) return;
    const size = Number(form.partySize);
    if (!Number.isFinite(size) || size < vendor.minPartySize || size > vendor.maxPartySize) {
      setProblem(`Numărul de persoane trebuie să fie între ${vendor.minPartySize} și ${vendor.maxPartySize}.`);
      return;
    }
    if (form.date.length === 0 || form.time.length === 0) {
      setProblem("Alege ziua și ora.");
      return;
    }
    if (form.name.trim().length === 0 || form.phone.trim().length === 0) {
      setProblem("Restaurantul are nevoie de numele și telefonul tău ca să confirme rezervarea.");
      return;
    }

    setSending(true);
    setProblem(null);
    try {
      const response = await createReservation(marketKey, vendor.vendorSlug, {
        date: form.date,
        time: form.time,
        partySize: size,
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim().length > 0 ? form.email.trim() : null,
        occasion: form.occasion.length > 0 ? form.occasion : null,
        notes: form.notes.trim().length > 0 ? form.notes.trim() : null,
      });
      setDone(response);
    } catch (error: unknown) {
      // Mesajul serverului se afiseaza asa cum vine: e in romana si stie ce s-a
      // intamplat. Ce adaugam noi nu presupune nicio cauza.
      setProblem(toApiError(error).message);
    } finally {
      setSending(false);
    }
  };

  const title = blockText(block, "title", "Rezervă o masă");
  const note = blockText(block, "note");

  // Blocul NU dispare cand nu are ce oferi.
  //
  // Un template livrat cu o pagina „Rezervări" in meniu produce, pe o piata ale
  // carei restaurante nu au rezervari online pornite, o pagina complet alba:
  // clientul o deschide, nu vede nimic si nu afla ca poate suna. Aceeasi tacere
  // acoperea si intervalul de incarcare, la fiecare vizita. Se spune ce se stie,
  // cu ce are clientul de facut.
  if (vendorsLoad.state === "loading") {
    return (
      <BlockSection id={block.id} labelledBy={`bloc-${block.id}`}>
        <BlockHeading id={`bloc-${block.id}`} title={title} level={headingLevel} />
        <div className="da-shimmer h-40 max-w-readable rounded-card" aria-hidden="true" />
      </BlockSection>
    );
  }
  if (vendorsLoad.state === "error" || vendors.length === 0) {
    return (
      <BlockSection id={block.id} labelledBy={`bloc-${block.id}`}>
        <BlockHeading id={`bloc-${block.id}`} title={title} level={headingLevel} />
        <Alert tone="info" title="Rezervările online nu sunt deschise acum">
          <p>
            {supportPhone.length > 0
              ? "Sună la restaurant ca să rezervi o masă."
              : "Contactează restaurantul ca să rezervi o masă."}
          </p>
          {supportPhone.length === 0 ? null : (
            <p className="mt-1">
              <a className="font-semibold underline" href={`tel:${supportPhone.replace(/\s+/g, "")}`}>
                {supportPhone}
              </a>
            </p>
          )}
        </Alert>
      </BlockSection>
    );
  }

  if (done !== null) {
    return (
      <BlockSection id={block.id} labelledBy={`bloc-${block.id}`}>
        <BlockHeading id={`bloc-${block.id}`} title={title} level={headingLevel} />
        <Alert
          tone={done.status === "confirmed" ? "success" : "info"}
          title={
            done.status === "confirmed"
              ? "Masa e rezervată"
              : "Cererea a ajuns la restaurant"
          }
        >
          <p>
            {done.status === "confirmed"
              ? `${done.vendorDisplayName} te așteaptă pe ${done.date} la ${done.time}, ${done.partySize} persoane.`
              : `${done.vendorDisplayName} a primit cererea pentru ${done.date}, ora ${done.time}, `
                + `${done.partySize} persoane, și îți confirmă telefonic.`}
          </p>
          <p className="mt-1">{`Număr rezervare: ${done.reference}`}</p>
          {done.message === null || done.message === undefined || done.message.length === 0 ? null : (
            <p className="mt-1">{done.message}</p>
          )}
        </Alert>
      </BlockSection>
    );
  }

  return (
    <BlockSection id={block.id} labelledBy={`bloc-${block.id}`}>
      <BlockHeading
        id={`bloc-${block.id}`}
        title={title}
        subtitle={blockText(block, "subtitle")}
        level={headingLevel}
      />

      <div className="da-card flex max-w-readable flex-col gap-4 p-4 sm:p-5">
        {vendors.length < 2 ? null : (
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-ink">Unde</span>
            <select
              className="w-full rounded-control border border-line bg-canvas px-3 py-2.5 text-base text-ink"
              value={vendorSlug}
              onChange={(event) => setVendorSlug(event.target.value)}
            >
              {vendors.map((item) => (
                <option key={item.vendorSlug} value={item.vendorSlug}>
                  {item.city === null || item.city === undefined || item.city.length === 0
                    ? item.displayName
                    : `${item.displayName} · ${item.city}`}
                </option>
              ))}
            </select>
          </label>
        )}

        {!windowClosed ? null : (
          <Alert tone="info" title="Nu mai sunt zile disponibile">
            {vendor === null
              ? null
              : `${vendor.displayName} primește rezervări cu cel puțin ${vendor.minAdvanceHours} ore înainte, `
                + "iar fereastra de azi s-a închis. Încearcă mâine sau sună la restaurant."}
          </Alert>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label="Ziua"
            type="date"
            required
            min={minDate}
            max={maxDate}
            value={form.date}
            onChange={(event) => set("date", event.target.value)}
          />
          <Field
            label="Persoane"
            type="number"
            required
            min={vendor?.minPartySize ?? 1}
            max={vendor?.maxPartySize ?? 12}
            value={form.partySize}
            onChange={(event) => set("partySize", event.target.value)}
            hint={vendor === null ? undefined : `Între ${vendor.minPartySize} și ${vendor.maxPartySize} persoane.`}
          />
        </div>

        {form.date.length === 0 || !partySizeValid ? (
          <p className="text-sm text-ink-muted">Alege ziua și numărul de persoane ca să vezi orele libere.</p>
        ) : slotsLoad.state === "loading" ? (
          <p className="text-sm text-ink-muted">Se verifică orele libere…</p>
        ) : slotsLoad.state === "error" ? (
          <Alert tone="warning" title="Orele libere nu au putut fi citite">
            {slotsLoad.error?.message}
          </Alert>
        ) : freeSlots.length === 0 ? (
          <p className="text-sm text-ink-muted">
            În ziua asta nu mai e nicio oră liberă pentru numărul de persoane ales. Încearcă altă zi.
          </p>
        ) : (
          <fieldset className="flex flex-col gap-2">
            <legend className="text-sm font-medium text-ink">Ora</legend>
            <div className="flex flex-wrap gap-2">
              {freeSlots.map((slot) => (
                <button
                  key={slot.time}
                  type="button"
                  onClick={() => set("time", slot.time)}
                  aria-pressed={form.time === slot.time}
                  className={[
                    "da-tap rounded-control border px-3 py-2 text-sm font-semibold",
                    form.time === slot.time
                      ? "border-transparent bg-brand text-brand-contrast"
                      : "border-line bg-canvas text-ink hover:bg-surface-hover",
                  ].join(" ")}
                >
                  {slot.time}
                </button>
              ))}
            </div>
          </fieldset>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Numele tău" required value={form.name} onChange={(event) => set("name", event.target.value)} />
          <Field
            label="Telefon"
            type="tel"
            required
            inputMode="tel"
            value={form.phone}
            onChange={(event) => set("phone", event.target.value)}
          />
        </div>
        <Field
          label="Email"
          type="email"
          value={form.email}
          onChange={(event) => set("email", event.target.value)}
          hint="Pentru confirmare scrisă, dacă restaurantul o trimite."
        />

        {vendor === null || vendor.occasions.length === 0 ? null : (
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-ink">
              Ocazia
              <span className="font-normal text-ink-muted"> (opțional)</span>
            </span>
            <select
              className="w-full rounded-control border border-line bg-canvas px-3 py-2.5 text-base text-ink"
              value={form.occasion}
              onChange={(event) => set("occasion", event.target.value)}
            >
              <option value="">Fără</option>
              {vendor.occasions.map((occasion) => (
                <option key={occasion} value={occasion}>
                  {occasion}
                </option>
              ))}
            </select>
          </label>
        )}

        <TextArea
          label="Mențiuni pentru restaurant"
          value={form.notes}
          onChange={(event) => set("notes", event.target.value)}
        />

        {vendor?.requiresDeposit !== true ? null : (
          <Alert tone="info" title="Restaurantul cere un avans">
            Suma și modul de plată ți le comunică restaurantul la confirmare.
          </Alert>
        )}

        {problem === null ? null : <Alert tone="error">{problem}</Alert>}

        <Button block loading={sending} onClick={() => void submit()}>
          Trimite cererea
        </Button>

        {vendor?.note === null || vendor?.note === undefined || vendor.note.length === 0 ? null : (
          <p className="text-sm text-ink-muted">{vendor.note}</p>
        )}
      </div>

      {note.length === 0 ? null : (
        <div className="flex max-w-readable flex-col gap-2 text-sm text-ink-muted">
          {paragraphs(note).map((part, index) => (
            <p key={index}>{part}</p>
          ))}
        </div>
      )}
    </BlockSection>
  );
}
